import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, interval, of, switchMap, tap } from 'rxjs';
import { StateManager } from '../state-manager/state-manager.service';
import { isDifferentBuild } from './app-version-comparison';

export interface AppVersion {
  version?: string;
  buildNumber?: string;
  commit?: string;
  buildTime?: string;
}

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService {
  private readonly versionUrl = 'assets/version/version.json';
  private readonly STORAGE_KEY = 'app_versions';
  private readonly APP_NAME = 'swift';
  //TODO: Add robust polling mechanism. For now, it's a simple fixed-interval polling.
  private readonly pollingIntervalMs = 0.5 * 60 * 1000; // 2 minutes
  stateManager = inject(StateManager);
  readonly localVersion = signal<AppVersion | undefined>(this.readLocalVersionFromStorage());
  readonly latestVersion = signal<AppVersion | undefined>(undefined);
  readonly hasUpdate = signal<boolean>(false);

  constructor(private http: HttpClient) {}

  startMonitoring(): void {
    if (this.localVersion()) {
      this.startLatestVersionPolling();
      return;
    }

    this.fetchLocalVersion()
      .pipe(
        catchError(() => {
          this.localVersion.set(undefined);
          return of(null);
        })
      )
      .subscribe(() => {
        this.startLatestVersionPolling();
      });
  }

  private startLatestVersionPolling(): void {
    // fetch latest version immediately on load
    this.fetchLatestVersion().subscribe(() => this.checkForNewVersion());

    // start periodic polling
    interval(this.pollingIntervalMs)
      .pipe(switchMap(() => this.fetchLatestVersion()))
      .subscribe(() => this.checkForNewVersion());
  }

  /** First visit: the build being served is the one the user is running, so store it as the seen baseline. */
  private fetchLocalVersion() {
    return this.http.get<AppVersion>(this.versionUrl).pipe(
      tap(v => {
        this.localVersion.set(v);
        this.saveLocalVersionToStorage(v);
      })
    );
  }

  private fetchLatestVersion() {
    const cacheBust = Date.now().toString();

    return this.http
      .get<AppVersion>(this.versionUrl, {
        params: { cb: cacheBust },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      })
      .pipe(
        tap(v => {
          this.latestVersion.set(v);
        }),
        catchError(() => {
          this.latestVersion.set(undefined);
          return of(null);
        })
      );
  }

  private checkForNewVersion(): void {
    const latest = this.latestVersion();
    const local = this.localVersion();
    // Without both versions there is nothing to compare, so never claim an update.
    this.hasUpdate.set(!!latest && !!local && isDifferentBuild(local, latest));
  }

  private readLocalVersionFromStorage(): AppVersion | undefined {
    const raw = this.stateManager[this.STORAGE_KEY];
    if (!raw) return undefined;

    try {
      const store = JSON.parse(raw) as Record<string, AppVersion>;
      return store[this.APP_NAME];
    } catch {
      return undefined;
    }
  }

  private saveLocalVersionToStorage(version: AppVersion): void {
    const raw = this.stateManager[this.STORAGE_KEY];

    let store: Record<string, AppVersion> = {};

    if (raw) {
      try {
        store = JSON.parse(raw);
      } catch {
        store = {};
      }
    }

    store[this.APP_NAME] = version;
    this.stateManager.update(this.STORAGE_KEY, store, false);
  }

  markLatestVersionAsSeen(): void {
    const latest = this.latestVersion();
    if (!latest) {
      return;
    }

    // Use localVersion as the seen marker so no extra state is required.
    this.localVersion.set(latest);
    this.saveLocalVersionToStorage(latest);
    this.hasUpdate.set(false);
  }

  getVersionDialogData(): {
    currentVersion?: AppVersion;
    latestVersion?: AppVersion;
  } {
    return {
      currentVersion: this.localVersion(),
      latestVersion: this.latestVersion(),
    };
  }

  async reloadToLatest(): Promise<void> {
    await this.clearAllCaches();
    this.hardReload();
  }

  /**
   * Clears all application caches including:
   * - Browser Cache API
   * - Service Worker caches
   * - localStorage
   * - sessionStorage
   */
  async clearAllCaches(): Promise<void> {
    await Promise.all([
      this.clearCacheStorage(),
      /// this.clearLocalStorage(),
      //  this.clearSessionStorage(),
      this.unregisterServiceWorkers(),
    ]);
  }
  /** Clears Cache Storage API (used by Service Workers) */
  async clearCacheStorage(): Promise<void> {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
  }
  /** Clears localStorage */
  clearLocalStorage(): void {
    this.stateManager.clear();
  }
  /** Clears sessionStorage */
  clearSessionStorage(): void {
    sessionStorage.clear();
  }
  /** Unregisters all Service Workers */
  async unregisterServiceWorkers(): Promise<void> {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }
  }
  /** Force reload bypassing browser cache */
  hardReload(): void {
    window.location.reload();
  }
}
